import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Song } from "@/javascript/library-objects";
import { Utils } from "@/javascript/utils";
import { Box, Button, Flex, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useContext } from "react";
import ConductionModal from "../ConductionModal";

interface SongDeletionFormProps {
	song: Song;
	onClose: () => void;
	onDeleted: () => void;
}

const SongDeletionForm: React.FC<SongDeletionFormProps> = ({ song, onClose, onDeleted }) => {
	const auth = useContext(AuthContext);
	if (!auth) {
		throw new Error("AdminSongDeletionForm must be a child of AuthContext.");
	}
	const token = auth.token;
	if (!token) return null;

	// Set up the form; we only need one field where the user confirms the song title.
	const form = useForm({
		initialValues: {
			songTitle: "",
		},
		validate: {
			songTitle: (value) =>
				value.trim().length === 0
					? "You must type the song title to confirm deletion."
					: song !== null && value.trim() !== song.songName.trim() ?
						"Entered song name does not match."
						: null,
		},
	});

	const handleSubmit = async () => {
		if (!form.isValid()) {
			form.validate();
			return;
		}

		// Display a notification that deletion is in progress.
		const notificationId = Utils.displayNotification(
			"Deleting Song",
			"Deleting song, please wait...",
			"blue",
			"loading"
		);

		try {
			await ConductionAPI.deleteSong(song.songId, token);

			Utils.updateNotification(
				notificationId,
				"Song Deleted",
				"The song was deleted.",
				"green",
				<IconCheck size={16} />
			);

			onDeleted();
			onClose();
		} catch (error) {
			console.error("Error deleting song", error);
			Utils.updateNotification(
				notificationId,
				"Error",
				"Failed to delete the song.",
				"red",
				<IconAlertCircle size={16} />,
				true
			);
		}
	};

	const height = 20; // view height (vh)

	return (
		<ConductionModal
			onClose={onClose}
			title="Delete Song"
			size="md"
		>
			<Box
				style={{
					minHeight: `${height}vh`
				}}
			>
				{
					<form onSubmit={form.onSubmit(handleSubmit)}>
						{(song.processed) && (
							<Text c="red" td="underline">
								This song is fully processed. Are you sure you want to delete it?
							</Text>
						)}
						<Flex
							direction={"column"}
							justify={"space-between"}
							align={"stretch"}
							style={{
								minHeight: `${height}vh`
							}}
						>
							<Stack>
								<Text>
									Please type the exact name of the song, "{song.songName}", to confirm deletion:
								</Text>
								<TextInput
									label="Song Title"
									placeholder={song.songName}
									required
									{...form.getInputProps("songTitle")}
								/>
							</Stack>
							<Button
								type="submit"
								radius="xl"
								size='sm'
								loading={form.submitting}
								color="red"
							>
								Delete Song
							</Button>
						</Flex>
					</form>
				}
			</Box>
		</ConductionModal>
	);
};

export default SongDeletionForm;
